import React, { useEffect, useState } from 'react';
import usePlacesAutocomplete, { getDetails, getGeocode } from 'use-places-autocomplete';
import { Field, TextInput, Autocomplete } from '../onyx/Field';

/**
 * Parse Google Places / Geocoder address_components into form address fields.
 */
export function parseAddressComponents(components) {
  let streetNumber = '';
  let route = '';
  let city = '';
  let state = '';
  let zipCode = '';
  let subpremise = '';

  (components || []).forEach((component) => {
    const types = component.types || [];
    if (types.includes('street_number')) streetNumber = component.long_name;
    if (types.includes('route')) route = component.long_name;
    if (types.includes('locality')) city = component.long_name;
    if (types.includes('postal_town') && !city) city = component.long_name;
    if (types.includes('sublocality_level_1') && !city) city = component.long_name;
    if (types.includes('neighborhood') && !city) city = component.long_name;
    if (types.includes('administrative_area_level_1')) state = component.short_name;
    if (types.includes('postal_code')) zipCode = component.long_name;
    if (types.includes('subpremise')) subpremise = component.long_name;
  });

  return {
    address_1: `${streetNumber} ${route}`.trim(),
    address_2: subpremise || undefined,
    city,
    state,
    zip_code: zipCode,
  };
}

async function resolvePlaceComponents(suggestion) {
  const placeId = suggestion?.place_id;
  if (placeId) {
    try {
      const details = await getDetails({
        placeId,
        fields: ['address_components', 'formatted_address'],
      });
      if (details?.address_components?.length) {
        return details.address_components;
      }
    } catch (err) {
      console.warn('Place details failed, falling back to geocode:', err);
    }
  }

  const description = typeof suggestion === 'string'
    ? suggestion
    : (suggestion?.description || '');
  if (!description) {
    throw new Error('No address to resolve');
  }
  const results = await getGeocode({ address: description });
  return results[0]?.address_components || [];
}

/**
 * Address line 1 with Google Places autocomplete (Onyx chrome).
 *
 * Init is deferred until ``mapsLoaded``. Selection resolves via Place Details
 * (place_id) with Geocoding fallback, then patches all address fields in one
 * ``onFieldChange`` call so React state updates are not overwritten.
 */
export function AddressAutocompleteInput({
  value,
  onFieldChange,
  mapsLoaded,
  idPrefix = 'address',
  required = true,
}) {
  const address = value && typeof value === 'object' ? value : {};
  const line1 = address.address_1 || '';
  const [listOpen, setListOpen] = useState(false);

  const {
    ready,
    value: placesValue,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['address'],
    },
    debounce: 300,
    defaultValue: line1,
    initOnMount: false,
  });

  useEffect(() => {
    if (mapsLoaded) {
      init();
    }
  }, [mapsLoaded, init]);

  // Keep the Places input in sync with parent prefills / external clears.
  useEffect(() => {
    if (line1 !== placesValue) {
      setValue(line1, false);
    }
  }, [line1]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setListOpen(status === 'OK' && data.length > 0);
  }, [status, data]);

  const applyAddressPatch = (patch) => {
    // Prefer a single object patch (QuestionRenderer merges in one setState).
    onFieldChange(patch);
  };

  const clearStructuredAddress = () => {
    setValue('', false);
    clearSuggestions();
    setListOpen(false);
    // Line-1 clear must drop autofilled city/state/zip (MUI clear only empties input).
    applyAddressPatch({
      address_1: '',
      city: '',
      state: '',
      zip_code: '',
    });
  };

  const handleSelect = async (suggestion) => {
    const description = typeof suggestion === 'string'
      ? suggestion
      : (suggestion?.description || '');
    setValue(description, false);
    clearSuggestions();
    setListOpen(false);

    try {
      const components = await resolvePlaceComponents(suggestion);
      const parsed = parseAddressComponents(components);
      const street = parsed.address_1 || description;
      setValue(street, false);
      const patch = {
        address_1: street,
        city: parsed.city,
        state: parsed.state,
        zip_code: parsed.zip_code,
      };
      if (parsed.address_2) {
        patch.address_2 = parsed.address_2;
      }
      applyAddressPatch(patch);
    } catch (error) {
      console.error('Error resolving address place:', error);
      applyAddressPatch({ address_1: description });
    }
  };

  if (!mapsLoaded) {
    return (
      <Field
        density="compact"
        label="Address Line 1"
        htmlFor={`${idPrefix}-address-1`}
        required={required}
        help="Enter your complete street address"
      >
        <TextInput
          density="compact"
          id={`${idPrefix}-address-1`}
          value={line1}
          onChange={(e) => onFieldChange('address_1', e.target.value)}
          placeholder="Enter your street address"
          required={required}
          autoComplete="street-address"
        />
      </Field>
    );
  }

  return (
    <Field
      density="compact"
      label="Address Line 1"
      htmlFor={`${idPrefix}-address-1`}
      required={required}
      help={
        ready
          ? 'Start typing to see address suggestions'
          : 'Loading address suggestions…'
      }
    >
      <Autocomplete
        className="preferences-address-autocomplete"
        options={data}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option;
          return option?.description || '';
        }}
        isOptionEqualToValue={(option, selected) => (
          option?.place_id === selected?.place_id
          || option?.description === selected
        )}
        filterOptions={(x) => x}
        freeSolo
        open={listOpen}
        onOpen={() => {
          if (data.length > 0) setListOpen(true);
        }}
        onClose={() => setListOpen(false)}
        inputValue={placesValue}
        value={null}
        onChange={(_event, newValue) => {
          if (newValue == null || newValue === '') {
            clearStructuredAddress();
            return;
          }
          if (typeof newValue === 'object') {
            handleSelect(newValue);
          } else if (typeof newValue === 'string' && newValue.trim()) {
            handleSelect(newValue.trim());
          }
        }}
        onInputChange={(_event, newInputValue, reason) => {
          // MUI fires "reset" when options sync — do not refetch / clear on that.
          if (reason !== 'input' && reason !== 'clear') {
            return;
          }
          if (reason === 'clear' || newInputValue === '') {
            clearStructuredAddress();
            return;
          }
          setValue(newInputValue);
          onFieldChange('address_1', newInputValue);
        }}
        renderInput={(params) => (
          <div
            ref={params.InputProps.ref}
            className="preferences-address-autocomplete__control"
          >
            <input
              {...params.inputProps}
              id={`${idPrefix}-address-1`}
              className="onyx-input onyx-input--compact"
              disabled={!ready}
              required={required}
              placeholder={ready ? 'Start typing your address…' : 'Loading…'}
              autoComplete="street-address"
            />
            {params.InputProps.endAdornment}
          </div>
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.place_id}>
            {option.description}
          </li>
        )}
        noOptionsText={
          status === 'OK' ? 'No addresses found' : 'Start typing to search…'
        }
        slotProps={{
          paper: { className: 'preferences-address-autocomplete__paper' },
          popper: { className: 'preferences-address-autocomplete__popper' },
        }}
      />
    </Field>
  );
}
